import { makeAutoObservable, runInAction, toJS } from 'mobx';

import { utils } from "./Utils";
import { userAjaxController } from "../Ajax/UserAjaxController";
import { APIUserDTO } from "../DTOs/APIUserDTO";
import { StockistCoverAlertFrequency, StockistDTO } from '../DTOs/StockistDTO';
import { basketController } from "./BasketController";
import { addressesController } from './AddressesController';
import { issueAjaxController } from '../Ajax/IssueAjaxController';
import { StockistIssue } from '../DTOs/StockistIssue';
import { StockistPurchaseModel } from '../DTOs/Purchase/StockistPurchaseModel';
import { orderAjaxController } from '../Ajax/OrderAjaxController';
import { ajaxController } from "../Ajax/AjaxController";
import { countryController } from './CountryController';
import { Zone } from '../DTOs/Address/Country';
import { alertController } from './AlertController';

export class UserAccountController {
  currentUser: APIUserDTO | null = null;
  _stockistUserId: number | null = null;
  _stockist: StockistDTO | null = null;
  workingStockist: StockistDTO | null = null;
  issuesForExtras: StockistIssue[] = [];
  userOrders: StockistPurchaseModel[] = [];
  rememberUser = false;
  isUserAccountDataLoading = true;
  settingsDraft: {
    coverAlertsFrequency: StockistCoverAlertFrequency;
  } | null = null;

  private accountIdStorageKey = "account"
  private apiTokenStorageKey = "jwt"
  private userAjaxController = userAjaxController;

  get stockist() {
    return this._stockist
  }

  set stockist(value: StockistDTO | null) {
    this._stockist = value
  }

  onStockistNameChange(value: string) {
    if (this.workingStockist)
      this.workingStockist.stockistName = value;
  }

  onContactFirstNameChange(value: string) {
    if (this.workingStockist)
      this.workingStockist.contactFirstName = value;
  }

  onContactSurnameChange(value: string) {
    if (this.workingStockist)
      this.workingStockist.contactSurname = value;
  }

  onContactNumberChange(value: string) {
    if (this.workingStockist)
      this.workingStockist.contactTel = value;
  }

  onVatNumberChange(value: string) {
    if (this.workingStockist)
      this.workingStockist.vatNo = value;
  }

  onEoriNumberChange(value: string) {
    if (this.workingStockist)
      this.workingStockist.eori = value;
  }

  onCoverAlertsFrequencyChange(value: number) {
    if (this.workingStockist)
      this.workingStockist.coverAlertsFrequency = value;
  }

  // TODO: all the validators could be moved to single file
  get stockistNameInputError() {
    if (this.workingStockist) {
      return !!this.workingStockist.stockistName && this.workingStockist.stockistName.length < 2;
    }

    return false;
  }

  get contactFirstNameInputError() {
    if (this.workingStockist) {
      return !!this.workingStockist.contactFirstName && this.workingStockist.contactFirstName.length < 2;
    }

    return false;
  }

  get contactSurnameNameInputError() {
    if (this.workingStockist) {
      return !!this.workingStockist.contactSurname && this.workingStockist.contactSurname.length < 2
    }

    return false;
  }

  get contactNumberInputError() {
    if (this.workingStockist) {
      return !!this.workingStockist.contactTel && !utils.phoneNumberCorrect(this.workingStockist.contactTel)
    }

    return false;
  }

  get vatNumberInputError() {
    if (this.workingStockist) {
      return !!this.workingStockist.vatNo && this.workingStockist.vatNo.length < 2
    }

    return false;
  }

  get billingAddressCountry() {
    return countryController.countries.find(c => c.name === addressesController.billingAddress?.country || c.iso_a2 === addressesController.billingAddress?.country);
  }

  get isEoriRequired() {
    const zone = this.billingAddressCountry?.zone;
    return zone === Zone.eu || zone === Zone.denmark;
  }

  get eoriNumberInputError() {
    if (!this.workingStockist || (!this.isEoriRequired && !this.workingStockist.eori)) return false;
    return !!this.workingStockist.eori && (this.workingStockist.eori.length < 2 || this.workingStockist.eori.length > 18);
  }

  get saveButtonDisabled() {
    const hasValidationErrors =
      this.stockistNameInputError || !this.workingStockist?.stockistName ||
      this.contactFirstNameInputError || !this.workingStockist?.contactFirstName ||
      this.contactSurnameNameInputError || !this.workingStockist?.contactSurname ||
      this.contactNumberInputError || !this.workingStockist?.contactTel ||
      this.vatNumberInputError || !this.workingStockist?.vatNo ||
      this.eoriNumberInputError;

    return hasValidationErrors || !this.hasUnsavedChanges;
  }

  startEditingSettings() {
    this.settingsDraft = {
      coverAlertsFrequency: this.workingStockist?.coverAlertsFrequency || 0,
    }
  }

  applySettingsChanges() {
    if (this.settingsDraft && this.workingStockist) {
      this.workingStockist.coverAlertsFrequency = this.settingsDraft.coverAlertsFrequency;
    }
  }

  get isSettingsDirty() {
    if (!this.settingsDraft) return false;

    return (
      this.settingsDraft.coverAlertsFrequency !==
      this.workingStockist?.coverAlertsFrequency
    );
  }

  get isLoggedIn() {
    return !!this.currentUser;
  }

  get isActivated() {
    return !!this.currentUser && this.currentUser.isLive;
  }

  get apiKey() {
    return utils.loadFromLocalStorage(this.apiTokenStorageKey) || utils.loadFromSessionStorage(this.apiTokenStorageKey)
  }

  setWorkingStockist() {
    if (this.stockist) {
      this.workingStockist = new StockistDTO(toJS(this.stockist));
    }
  }

  resetWorkingStockist() {
    this.workingStockist = null;
  }

  commitWorkingStockist() {
    if (this.workingStockist) {
      this.stockist = new StockistDTO(toJS(this.workingStockist));
    }
  }

  get hasUnsavedChanges() {
    if (!this._stockist || !this.workingStockist) return false;

    return (
      this._stockist.stockistName !== this.workingStockist.stockistName ||
      this._stockist.contactFirstName !== this.workingStockist.contactFirstName ||
      this._stockist.contactSurname !== this.workingStockist.contactSurname ||
      this._stockist.contactTel !== this.workingStockist.contactTel ||
      this._stockist.vatNo !== this.workingStockist.vatNo ||
      this._stockist.eori !== this.workingStockist.eori ||
      this._stockist.coverAlertsFrequency !== this.workingStockist.coverAlertsFrequency
    );
  }

  validateUser = async (
    stockistName: string,
    email: string,
    password: string,
    firstName: string,
    surname: string,
    companyName: string,
    address1: string,
    address2: string,
    address3: string,
    address4: string,
    country: string,
    postCode: string,
    contactNumber: string,
    VATNo: string,
    eori: string,
    stockistType: string,
    coverAlertsFrequency: StockistCoverAlertFrequency
  ) => {
    return await this.userAjaxController.validateUser(
      {
        stockistName: stockistName,
        email: email,
        password: password,
        billingAddress: {
          firstName: firstName,
          surname: surname,
          companyName: companyName,
          address1: address1,
          address2: address2,
          address3: address3,
          address4: address4,
          postCode: postCode,
          country: country,
          email: email,
          inUse: true,
          id: 0,
          description: '',
          phoneDay: contactNumber,
        },
        contactFirstName: firstName,
        contactSurname: surname,
        contactTel: contactNumber,
        VATNo: VATNo,
        eori: eori,
        stockistType,
        coverAlertsFrequency
      }
    );
  }

  validateInitialUserData = async (
    stockistName: string,
    stockistType: string,
    email: string,
    password: string,
    firstName: string,
    surname: string,
    VATNo: string
  ) => {
    return await this.userAjaxController.validateInitialUserInfo(
      {
        stockistName,
        stockistType,
        email,
        password,
        contactFirstName: firstName,
        contactSurname: surname,
        VATNo
      }
    )
  }

  signUp = async (
    stockistName: string,
    email: string,
    password: string,
    firstName: string,
    surname: string,
    companyName: string,
    address1: string,
    address2: string,
    address3: string,
    address4: string,
    country: string,
    postCode: string,
    contactNumber: string,
    VATNo: string,
    eori: string,
    stockistType: string,
    coverAlertsFrequency: StockistCoverAlertFrequency
  ) => {
    const apiUser = await this.userAjaxController.createUser(
      {
        stockistName: stockistName,
        email: email,
        password: password,
        billingAddress: {
          firstName: firstName,
          surname: surname,
          companyName: companyName,
          address1: address1,
          address2: address2,
          address3: address3,
          address4: address4,
          postCode: postCode,
          country: country,
          email: email,
          inUse: true,
          id: 0,
          description: 'Billing Address',
          phoneDay: contactNumber,
        },
        contactFirstName: firstName,
        contactSurname: surname,
        contactTel: contactNumber,
        VATNo: VATNo,
        eori: eori,
        stockistType,
        coverAlertsFrequency
      }
    );

    if (apiUser) {
      await this.saveStockistLocally(apiUser)
      return apiUser
    }
  }

  login = async (username: string, password: string, rememberUser: boolean) => {
    const apiUser = await this.userAjaxController.login(username, password)

    if (apiUser) {
      runInAction(() => this.rememberUser = rememberUser)
      await this.saveStockistLocally(apiUser)
    }
    return this.currentUser
  }

  logout = async (): Promise<boolean> => {
    const logoutSuccessful = true

    runInAction(() => this.currentUser = null);
    utils.removeFromLocalStorage(this.accountIdStorageKey);
    utils.removeFromLocalStorage(this.apiTokenStorageKey);
    utils.removeFromSessionStorage(this.accountIdStorageKey);
    utils.removeFromSessionStorage(this.apiTokenStorageKey);
    await basketController.clearBasket();
    return logoutSuccessful
  }

  saveStockistLocally = async (user: APIUserDTO) => {
    const saveSuccessful = true

    runInAction(() => this.currentUser = user)
    if (this.rememberUser) {
      utils.saveToLocalStorage(this.apiTokenStorageKey, user.apiToken)
      utils.saveToLocalStorage(this.accountIdStorageKey, user.id.toString())
    } else {
      utils.saveToSessionStorage(this.apiTokenStorageKey, user.apiToken)
      utils.saveToSessionStorage(this.accountIdStorageKey, user.id.toString())
    }

    if (this.apiKey)
      ajaxController.setJwtToken(this.apiKey)
    return saveSuccessful
  }

  getApiUser = async (userId: number) => {
    try {
      const user = await this.userAjaxController.getApiUser(userId)

      runInAction(() => {
        this.currentUser = user
      })
    }
    catch (e) {
      alertController.displayAlert("Access token expired", "You have been logged out", "warning");
      await this.logout();
    }
  }

  initializeUser = async () => {
    runInAction(() => {
      this.isUserAccountDataLoading = true;
    });

    try {
      const userId =
        utils.loadFromLocalStorage(this.accountIdStorageKey) ||
        utils.loadFromSessionStorage(this.accountIdStorageKey);

      if (userId && this.apiKey) {
        ajaxController.setJwtToken(this.apiKey);
        await this.getApiUser(parseInt(userId));
        await this.getStockist();
        await basketController.getBasket();
        await addressesController.fetchAddresses();
        runInAction(() => this.rememberUser = true)
      }
    } finally {
      runInAction(() => {
        this.isUserAccountDataLoading = false;
      });
    }
  }

  getStockist = async () => {
    if ((!this._stockist || this.currentUser?.id != this._stockistUserId)
      && this.currentUser instanceof APIUserDTO) {
      const stockist = await userAjaxController.getStockist(this.currentUser.id);
      const stockistUserId = this.currentUser.id;

      runInAction(() => {
        this.stockist = stockist
        this._stockistUserId = stockistUserId
      })
    }
  }

  async saveStockistEdits() {
    if (!this._stockist) return;

    const stockist = await this.userAjaxController.updateUser({
      stockistName: this._stockist.stockistName,
      email: this._stockist.email,
      contactFirstName: this._stockist.contactFirstName,
      contactSurname: this._stockist.contactSurname,
      contactTel: this._stockist.contactTel,
      VATNo: this._stockist.vatNo,
      eori: this._stockist.eori,
      coverAlertsFrequency: this.settingsDraft?.coverAlertsFrequency || 0
    });

    runInAction(() => this._stockist = stockist);
  }

  async getIssuesForExtras() {
    if (this.currentUser instanceof APIUserDTO) {
      const issues = await issueAjaxController.getIssuesForExtras();
      runInAction(() => this.issuesForExtras = issues);
    }
  }

  constructor() {
    makeAutoObservable(this);
  }
}

export const userAccountController = new UserAccountController();